import React, { useState, useEffect, useCallback } from 'react';
// Import household services
import { listHouseholds as listNewClients, deleteHousehold, searchHouseholds } from '../services/householdService';
import { listJobSchedules } from '../services/scheduleService';
// Import icons
import { FaPlus, FaEdit, FaTrashAlt, FaSearch, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { terminology } from '../config/terminology';
import { debounce } from 'lodash';
import DeleteHouseholdModal from '../components/DeleteHouseholdModal';

const PAGE_SIZE = 30; // Define a constant for page size

// Rename component
const ClientsListPage = () => {
  const [clients, setClients] = useState([]); // Renamed state variable
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // For fetch errors
  const [apiError, setApiError] = useState(null); // For action errors (like delete)
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null); // Keep variable name generic for now
  const [schedulesToDelete, setSchedulesToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- Search State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // Default to all so inactive clients are listed too
  // --- End Search State ---

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [pageStartDocIdsHistory, setPageStartDocIdsHistory] = useState([null]);
  const [isPaginatingAction, setIsPaginatingAction] = useState(false); // True during pagination button click action
  // --- End Pagination State ---

  // Unified data fetching function
  const fetchClientsData = useCallback(async (term, pageDocIdForFetch, skipLoadingState = false) => {
    const normalizedTerm = (term || '').trim();

    if (!skipLoadingState) {
      setLoading(true);
    }
    setError(null);
    // setIsSearching is primarily for the text message, driven by term length
    if (normalizedTerm.length >= 3 && !skipLoadingState) {
      setIsSearching(true);
    }

    try {
      let apiData;
      if (normalizedTerm.length >= 3) {
        apiData = await searchHouseholds({ 
          searchString: normalizedTerm,
          pageSize: PAGE_SIZE,
          startAfterDocId: pageDocIdForFetch,
          status: statusFilter === 'all' ? undefined : statusFilter
        });
      } else if (normalizedTerm.length === 0) {
        apiData = await listNewClients({
          pageSize: PAGE_SIZE,
          startAfterDocId: pageDocIdForFetch,
          status: statusFilter === 'all' ? undefined : statusFilter
        });
      } else {
        if (!skipLoadingState) {
          setLoading(false);
          setIsSearching(false);
        }
        return { newClients: [], newNextPageDocId: null };
      }
      let filteredHouseholds = apiData.households || [];
      
      // Client-side filtering as fallback if API doesn't support status filter
      if (statusFilter !== 'all') {
        filteredHouseholds = filteredHouseholds.filter(household => 
          household.status === statusFilter
        );
      }
      
      return {
        newClients: filteredHouseholds,
        newNextPageDocId: apiData.nextPageDocId || null,
      };
    } catch (err) {
      setError(err.message || `Failed to load ${terminology.household.plural.toLowerCase()}.`);
      return { newClients: [], newNextPageDocId: null }; // Return empty on error
    } finally {
      if (!skipLoadingState) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  }, [statusFilter]);

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce(async (term) => {
      setCurrentPage(1);
      setPageStartDocIdsHistory([null]);
      // fetchClientsData is async and returns data - use false for skipLoadingState to show loading
      const result = await fetchClientsData(term, null, false);
      setClients(result.newClients);
      setNextPageDocId(result.newNextPageDocId);
    }, 500),
    [fetchClientsData, setCurrentPage, setPageStartDocIdsHistory, setClients, setNextPageDocId]
  );

  // Combined effect for both search term and status filter changes
  useEffect(() => {
    const handleFetch = () => {
      setCurrentPage(1);
      setPageStartDocIdsHistory([null]);
      debouncedSearchHandler(searchTerm);
    };
    
    handleFetch();
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [searchTerm, statusFilter, debouncedSearchHandler]);

  // Function to fetch clients (now primarily used for delete refresh)
  const fetchClients = async () => {
    const docIdForCurrentPageRefresh = pageStartDocIdsHistory[currentPage - 1];
    // For delete refresh, we do want to show loading state
    const result = await fetchClientsData(searchTerm, docIdForCurrentPageRefresh, false);
    setClients(result.newClients);
    setNextPageDocId(result.newNextPageDocId);
  };

  useEffect(() => {
    // Clear success/error messages
    let timer;
    if (successMessage || apiError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setApiError(null);
        if (successMessage && location.state?.message) {
            navigate(location.pathname, { replace: true, state: {} });
        }
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, apiError, navigate, location.pathname, location.state?.message]);

  // Handler for search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handler to clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Handler for status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    // Don't reset pagination here - let the useEffect handle it
  };

  // Function to fetch clients (extracted for reuse)
  const handleAddClient = () => {
    navigate('/households/add'); // Update path in App.js later
  };

  const handleEdit = (id) => {
    navigate(`/households/edit/${id}`);
  };

  const handleDelete = async (client) => {
    setClientToDelete(client);
    setApiError(null);
    setSchedulesToDelete([]);

    try {
      const result = await listJobSchedules({
        householdId: client.id,
        scheduleStatus: 'active',
        pageSize: 100,
      });
      setSchedulesToDelete(result.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules for household:', err);
      setSchedulesToDelete([]);
    } finally {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setClientToDelete(null);
    setSchedulesToDelete([]);
  };

  const confirmDelete = async () => {
    if (!clientToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteHousehold(clientToDelete.id);
      closeModal();
      const displayName = getDisplayName(clientToDelete);
      setSuccessMessage(`${terminology.household.singular} '${displayName}' deleted successfully.`);
      fetchClients();
    } catch (err) {
      setApiError(err.message || `Failed to delete ${terminology.household.singular.toLowerCase()}.`);
      closeModal();
    } finally {
      setIsDeleting(false);
    }
  };

  const getDisplayName = (client) => {
    return `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'N/A';
  };

  // Helper function to get status icon
  const getStatusIcon = (status) => {
    if (status === 'active') {
      return <FaCheckCircle className="text-green-500" title="Active" />;
    } else if (status === 'inactive') {
      return <FaTimesCircle className="text-red-500" title="Inactive" />;
    } else {
      return <FaTimesCircle className="text-gray-400" title="Unknown" />;
    }
  };

  // --- Pagination Handlers ---
  const handleNextPage = useCallback(async () => {
    if (!nextPageDocId || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const docIdForNextFetch = nextPageDocId;

    try {
      // Pass skipLoadingState=true to maintain the current list while loading
      const result = await fetchClientsData(searchTerm, docIdForNextFetch, true);

      // Ensure result and newClients are valid before proceeding
      if (result && result.newClients && result.newClients.length > 0) {
        setClients(result.newClients);
        setNextPageDocId(result.newNextPageDocId); // This could be null if the new page is the last one
        setCurrentPage(prevPage => prevPage + 1);
        setPageStartDocIdsHistory(prevHistory => [...prevHistory, docIdForNextFetch]);
      } else {
        // Next page was empty or result was invalid
        // Mark that there's no further next page from this point
        setNextPageDocId(null);
        // Current page, clients list, and history remain unchanged
      }
    } catch (error) {
      // Error state is set by fetchClientsData. Log if needed or handle specific UI
      console.error("Error during pagination fetch:", error);
      setNextPageDocId(null); // Ensure next is disabled on error too
    } finally {
      setIsPaginatingAction(false);
    }
  }, [nextPageDocId, loading, searchTerm, fetchClientsData, setCurrentPage, setPageStartDocIdsHistory, setClients, setNextPageDocId]);

  const handlePrevPage = useCallback(async () => {
    if (currentPage <= 1 || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const targetPage = currentPage - 1;
    const docIdForPrevFetch = pageStartDocIdsHistory[targetPage - 1];

    try {
      // Pass skipLoadingState=true to maintain the current list while loading
      const result = await fetchClientsData(searchTerm, docIdForPrevFetch, true);
      
      // Even if result.newClients is empty (shouldn't happen for a valid prev page),
      // we update, as the user explicitly navigated back
      setClients(result.newClients || []); 
      setNextPageDocId(result.newNextPageDocId); 
      setCurrentPage(targetPage);
      setPageStartDocIdsHistory(prevHistory => prevHistory.slice(0, targetPage));
    } catch (error) {
      console.error("Error during previous page fetch:", error);
      // Potentially revert optimistic state updates or show error to user
    } finally {
      setIsPaginatingAction(false);
    }
  }, [currentPage, loading, pageStartDocIdsHistory, searchTerm, fetchClientsData, setCurrentPage, setPageStartDocIdsHistory, setClients, setNextPageDocId]);
  // --- End Pagination Handlers ---

  return (
    <div className="container mx-auto px-4 py-8">
       {/* Title and Back Button Row */}
       <div className="flex justify-between items-center mb-2">
         <h1 className="text-3xl font-bold text-jims-cyan-dark">{terminology.household.plural}</h1> {/* Use terminology */}
       </div>

       {/* Buttons and Search Row */}
       <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            {/* Add Button */}
            <button
                onClick={handleAddClient} // Use renamed handler
                className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
            >
                <FaPlus className="mr-2" />
                Add {terminology.household.singular} {/* Use terminology */}
            </button>

            <div className="flex flex-wrap items-center gap-4">
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
                  Status:
                </label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="border border-gray-300 rounded-md py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-transparent"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Search Input */}
              <div className="relative flex-grow sm:flex-grow-0 sm:w-64 md:w-72">
                  <input
                      type="text"
                      placeholder={`Search ${terminology.household.plural.toLowerCase()}...`} // Use terminology
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="border border-gray-300 rounded-md py-2 px-4 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-transparent"
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {searchTerm && (
                      <button
                          onClick={clearSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          aria-label="Clear search"
                      >
                          <FaTimes />
                      </button>
                  )}
              </div>
            </div>
       </div>

      {/* Display Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage} {/* Message already includes terminology */}
        </div>
      )}
      {/* Display API Error Message */}
      {apiError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {apiError} {/* Message already includes terminology */}
        </div>
      )}

      {/* Loading, Error, and Search States */}
      {(loading || isSearching) && !isPaginatingAction && clients.length === 0 && (
        <p className="text-center text-gray-500 py-4">
          {isSearching ? `Searching ${terminology.household.plural.toLowerCase()}...` : `Loading ${terminology.household.plural.toLowerCase()}...`}
        </p>
      )}
      {error && !apiError && (
        <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>
      )}
      {!loading && !isSearching && !isPaginatingAction && searchTerm.trim().length > 0 && searchTerm.trim().length < 3 && (
          <p className="text-center text-gray-500 p-6">Please enter at least 3 characters to search.</p>
      )}

      {!loading && !error && !(searchTerm.trim().length > 0 && searchTerm.trim().length < 3) && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {clients.length === 0 ? ( // Use renamed state variable
            <p className="text-center text-gray-500 p-6">
                {searchTerm.trim() ? `No ${terminology.household.plural.toLowerCase()} found matching your search.` : `No ${terminology.household.plural.toLowerCase()} found.`}
            </p> // Use terminology
          ) : (
            <table className="min-w-full leading-normal">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                  <th className="px-5 py-3 border-b-2 border-gray-200 w-12">Status</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">Name</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">Address</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">{terminology.client.singular} Name</th> {/* Use terminology for Supplier */}
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => ( // Use renamed state variable
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                      {getStatusIcon(client.status)}
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{getDisplayName(client)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-xs">
                      <p className="text-gray-900 whitespace-no-wrap">{client.addressLine1 || 'N/A'}</p>
                      {(client.addressLine2 || client.state) && (
                        <p className="text-gray-500 whitespace-no-wrap">
                          {[client.addressLine2, client.state].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{client.clientName || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                      <button onClick={() => handleEdit(client.id)} className="text-blue-500 hover:text-blue-700 mr-3 transition duration-300">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDelete(client)} className="text-red-500 hover:text-red-700 transition duration-300">
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Pagination Controls - Show if there are clients or a next page is available */}
          {(clients.length > 0 || nextPageDocId) && (
            <div className="mt-6 flex justify-center items-center space-x-2 py-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1 || loading || isSearching}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
              >
                {isPaginatingAction && currentPage > 1 ? 'Loading...' : 'Previous'}
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage}
              </span>
              <button
                onClick={handleNextPage}
                disabled={!nextPageDocId || loading || isSearching}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
              >
                {isPaginatingAction && nextPageDocId ? 'Loading...' : 'Next'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Household Confirmation Modal */}
      <DeleteHouseholdModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        householdName={getDisplayName(clientToDelete || {})}
        schedules={schedulesToDelete}
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </div>
  );
};

// Rename export
export default ClientsListPage;
